import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '@/components/AdminLayout';
import api, { apiErrorMessage } from '@/lib/api';
import { toast } from 'sonner';
import { Loader2, Award, Calendar, User, AlertTriangle } from 'lucide-react';

const AMBER = '#F59E0B';
const RED = '#DC2626';
const VIOLET = '#7C6BEF';

/** Iter 48 · Landing page for the "Distintivos por vencer" badge on the
 * admin Panel Principal. Read-only list of cycles whose validity is within
 * the next 30 days, sorted by urgency (soonest first). */
export default function AdminExpiringCerts() {
    const [rows, setRows] = useState(null);

    useEffect(() => {
        api.get('/admin/certifications/expiring')
            .then((r) => setRows(r.data || []))
            .catch((e) => {
                toast.error(apiErrorMessage(e, 'Error al cargar distintivos por vencer'));
                setRows([]);
            });
    }, []);

    if (rows === null) {
        return (
            <AdminLayout title="Distintivos por vencer">
                <div className="py-20 text-center text-gray-500 text-sm">
                    <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3" /> Cargando…
                </div>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout title="Distintivos por vencer">
            <div className="mb-6">
                <div className="text-[11px] uppercase tracking-[0.2em] font-bold mb-1 text-gray-500">Panel Admin</div>
                <h1 className="font-display text-3xl font-black tracking-tight">Distintivos por vencer</h1>
                <p className="text-sm text-gray-500 mt-1">
                    Ciclos activos que expiran en los próximos 30 días. Considera renovar o notificar al cliente.
                </p>
            </div>

            {rows.length === 0 ? (
                <div className="p-12 border text-center text-sm text-gray-500" style={{ borderColor: 'var(--border)' }} data-testid="expiring-empty">
                    <Award className="w-10 h-10 mx-auto mb-3 opacity-40" />
                    Ningún distintivo vence en los próximos 30 días.
                </div>
            ) : (
                <div className="border overflow-x-auto" style={{ borderColor: 'var(--border)' }} data-testid="expiring-table">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left uppercase font-bold text-gray-500 text-[10px] border-b" style={{ borderColor: 'var(--border)' }}>
                                <th className="py-3 px-4">Cliente</th>
                                <th className="py-3 px-4">Distintivo</th>
                                <th className="py-3 px-4">Vence</th>
                                <th className="py-3 px-4">Días</th>
                                <th className="py-3 px-4">Estado</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => {
                                const urgent = r.days_left <= 7;
                                const color = urgent ? RED : AMBER;
                                return (
                                    <tr key={r.cycle_id} className="border-b hover:bg-black/[0.02] dark:hover:bg-white/5" style={{ borderColor: 'var(--border)' }} data-testid={`expiring-row-${r.cycle_id}`}>
                                        <td className="py-3 px-4">
                                            <div className="font-bold inline-flex items-center gap-2"><User className="w-3.5 h-3.5 text-gray-400" /> {r.client_name}</div>
                                            <div className="text-xs text-gray-500 ml-5">{r.client_email}</div>
                                        </td>
                                        <td className="py-3 px-4">
                                            <div className="font-bold text-sm">{r.distintivo_name || 'Distintivo'}</div>
                                        </td>
                                        <td className="py-3 px-4 whitespace-nowrap">
                                            <span className="inline-flex items-center gap-1.5 text-xs">
                                                <Calendar className="w-3.5 h-3.5 text-gray-400" />
                                                {new Date(r.expires_at).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })}
                                            </span>
                                        </td>
                                        <td className="py-3 px-4">
                                            <span className="inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase font-bold" style={{ background: `${color}22`, color }} data-testid={`days-left-${r.cycle_id}`}>
                                                {urgent && <AlertTriangle className="w-3 h-3" />}
                                                {r.days_left} día{r.days_left === 1 ? '' : 's'}
                                            </span>
                                        </td>
                                        <td className="py-3 px-4 text-xs uppercase font-bold text-gray-500">{r.status}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            <div className="mt-6 text-xs text-gray-500">
                <Link to="/admin" className="hover:underline" style={{ color: VIOLET }}>← Volver al Panel Principal</Link>
            </div>
        </AdminLayout>
    );
}
